import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { Set } from '@wix/ambassador-members-v1-member/types';
import { MEMBERS_AREA, WIX_BLOG } from '@wix/app-definition-ids';
import type { IMembersAreaWidgetPluginService } from '@wix/members-area-widget-plugin-lib/viewer';
import {
  createMembersAreaWidgetPluginService,
  routerDataServiceFactory,
} from '@wix/members-area-widget-plugin-lib/viewer';
import type {
  ISettingsParam,
  IStyleParam,
  SettingsParamType,
  StyleParamType,
} from '@wix/tpa-settings';
import type { ControllerParams } from '@wix/yoshi-flow-editor';

import { MembersAboutMiddlewareService } from '../../services/members-about-middleware-service';
import { toNumberOrDefault } from '../../services/number-converter';
import { initWarmupDataService } from '../../services/warmup-data';
import type { Store } from '../../store';
import { createStore, getActionHandlers } from '../../store';
import type { RootState } from '../../store/root-reducer';
import type {
  ControllerContext,
  ControllerProps,
  FlowApi,
  InitialDataRequest as InitialDataRequestOptions,
  MembersAboutMiddlewareService as IMembersAboutMiddlewareService,
  MembersAreaApi,
  MemberSEOData,
  Site,
  WarmupDataService,
  WixCodeApi,
} from '../../types';
import { Experiment, Toast, WarmupDataKey } from '../../types';
import {
  createSettingsListener,
  registerControllerListeners,
  registerUserListeners,
  registerWidgetPluginHostListeners,
} from './controller-listeners';
import settingsParams, { defaultPostListPageSize } from './settingsParams';
import stylesParams from './stylesParams';
import { getIsBlogContributor } from './Widget/BlogPosts/utils';

type MaybeCallRenderSeoTagsOptions = {
  flowAPI: FlowApi;
  wixCodeApi: WixCodeApi;
  seoData: MemberSEOData | null;
  isViewer: boolean;
  isWidgetPlugin: boolean;
  site: Site;
};

type RenderSEOTagsOptions = {
  site: Site;
  seoData: MemberSEOData;
  wixCodeApi: WixCodeApi;
};

type GetInitialDataOptions = {
  flowAPI: FlowApi;
  widgetPluginService: IMembersAreaWidgetPluginService;
  warmupDataService: WarmupDataService;
  membersAboutMiddlewareService: IMembersAboutMiddlewareService;
};

const getMemberIds = async (
  flowApi: FlowApi,
  widgetPluginService: IMembersAreaWidgetPluginService,
) => {
  const { window, user } = flowApi.controllerConfig.wixCodeApi;
  const routerDataService = routerDataServiceFactory(
    widgetPluginService,
    user,
    window,
  );

  const routeData = await routerDataService.getRouteData();

  const currentMemberId = routeData?.currentMemberIdentifier?.id ?? null;
  const viewedMemberId = routeData?.viewedMemberIdentifier?.id ?? null;

  return { currentMemberId, viewedMemberId };
};

const getCreateBlogPostUrl = async (flowAPI: FlowApi) => {
  const { wixCodeApi } = flowAPI.controllerConfig;
  const { url, relativeUrl } = await wixCodeApi.site.getSectionUrl({
    appDefinitionId: WIX_BLOG,
    sectionId: 'post',
  });

  return {
    url: `${url}/create-post`,
    relativeUrl: `${relativeUrl}/create-post`,
  };
};

const isSectionVisible = (
  flowAPI: FlowApi,
  stylesParam: IStyleParam<StyleParamType.Boolean, boolean>,
  settingsParam: ISettingsParam<SettingsParamType.Boolean, boolean>,
): boolean => {
  const useMigratedStylesParams = flowAPI.experiments.enabled(
    Experiment.UseMigratedEditor3StylesParams,
  );
  const { settings } = flowAPI;
  const { styleParams: styles } = flowAPI.controllerConfig.config.style;

  const stylesVisibility = styles.booleans[stylesParam.key!];
  const settingsVisibility = settings.get(settingsParam);

  return useMigratedStylesParams
    ? stylesVisibility ?? settingsVisibility
    : settingsVisibility;
};

const getSectionVisibilityProps = (flowApi: FlowApi) => {
  const { settings } = flowApi;
  return {
    showAboutSection: isSectionVisible(
      flowApi,
      stylesParams.showAboutSection,
      settingsParams.showAboutSection,
    ),
    showBadgesSection: isSectionVisible(
      flowApi,
      stylesParams.showBadgesSection,
      settingsParams.showBadgesSection,
    ),
    showOverviewSection: isSectionVisible(
      flowApi,
      stylesParams.showOverviewSection,
      settingsParams.showOverviewSection,
    ),
    showBlogPostsSection: isSectionVisible(
      flowApi,
      stylesParams.showBlogPostsSection,
      settingsParams.showBlogPostsSection,
    ),
    showCommentsStats: isSectionVisible(
      flowApi,
      stylesParams.showPostCommentsCounter,
      settingsParams.showCommentsStats,
    ),
    showLikesReceivedStats: isSectionVisible(
      flowApi,
      stylesParams.showPostLikesCounter,
      settingsParams.showLikesReceivedStats,
    ),
    // This parameter is "legacy" (only used in forum)
    showTopCommentsStats: settings.get(settingsParams.showTopCommentsStats),
  };
};

const getShouldFetchFullMemberInfo = (
  flowAPI: FlowApi,
  widgetPluginService: IMembersAreaWidgetPluginService,
  showOverviewSection: boolean,
) => {
  const { controllerConfig } = flowAPI;
  const isWidgetPlugin = widgetPluginService.getIsWidgetPlugin();

  return (
    showOverviewSection ||
    isWidgetPlugin ||
    getIsMembersAreaSeoEnabled(flowAPI, controllerConfig.wixCodeApi)
  );
};

const fetchInitialState = async (
  flowAPI: FlowApi,
  widgetPluginService: IMembersAreaWidgetPluginService,
  membersAboutMiddlewareService: IMembersAboutMiddlewareService,
) => {
  const { settings } = flowAPI;
  const { language, multilingual, isRTL, isViewer } = flowAPI.environment;
  const { currentMemberId, viewedMemberId } = await getMemberIds(
    flowAPI,
    widgetPluginService,
  );
  const sectionVisibilityProps = isViewer
    ? getSectionVisibilityProps(flowAPI)
    : ({} as ReturnType<typeof getSectionVisibilityProps>);

  const shouldFetchFullMemberInfo = await getShouldFetchFullMemberInfo(
    flowAPI,
    widgetPluginService,
    sectionVisibilityProps.showOverviewSection,
  );

  const fetchMemberFieldSet = shouldFetchFullMemberInfo ? Set.FULL : Set.PUBLIC;

  if (viewedMemberId) {
    const payload: InitialDataRequestOptions = {
      ...sectionVisibilityProps,
      currentMemberId,
      viewedMemberId,
      postsPerPage: toNumberOrDefault(
        settings.get(settingsParams.postListPageSize),
        defaultPostListPageSize,
      ),
      language,
      isRTL,
      isMultilingualEnabled: multilingual?.isEnabled ?? false,
      fetchMemberFieldSet,
    };

    return membersAboutMiddlewareService.getInitialData(payload);
  }

  return {
    member: {} as any,
    badges: [],
    site: {
      sameSessionMember: false,
      isForumInstalled: false,
      isBlogInstalled: false,
    },
    blogPosts: { paging: { count: 0, offset: 0, total: 0 } },
    seoData: null,
  };
};

type FetchInitialStateResponse = Awaited<ReturnType<typeof fetchInitialState>>;

export const getInitialData = async ({
  flowAPI,
  membersAboutMiddlewareService,
  widgetPluginService,
  warmupDataService,
}: GetInitialDataOptions): Promise<{
  initialState: RootState;
  seoData: MemberSEOData | null;
}> => {
  let data: FetchInitialStateResponse;

  if (flowAPI.environment.isSSR) {
    data = await fetchInitialState(
      flowAPI,
      widgetPluginService,
      membersAboutMiddlewareService,
    );
    warmupDataService.set(WarmupDataKey.InitialData, data);
  } else {
    const warmupData = warmupDataService.get<FetchInitialStateResponse>(
      WarmupDataKey.InitialData,
    );

    data =
      warmupData ??
      (await fetchInitialState(
        flowAPI,
        widgetPluginService,
        membersAboutMiddlewareService,
      ));
  }

  const createBlogPostUrl = data.site.isBlogInstalled
    ? await getCreateBlogPostUrl(flowAPI)
    : { url: '', relativeUrl: '' };

  return {
    initialState: {
      member: data.member,
      badges: data.badges,
      site: data.site,
      blogPosts: {
        ...data.blogPosts,
        createBlogPostUrl,
        arePostsLoading: false,
      },
      ui: {
        isPublicProfilePreview: false,
        toast: {
          type: Toast.None,
          isVisible: false,
        },
        publishButton: {
          isDisabled: false,
        },
      },
      appSettings: {
        tabOpened: null,
        sectionState: null,
        sectionOpened: null,
      },
    },
    seoData: data.seoData,
  };
};

/**
 * Copy-pasted code from the @wix/ricos-common
 * Not used package by itself because it adds 30 kb to the bundle
 * https://github.com/wix-private/wix-ricos/blob/master/wix-ricos-common/src/utils/biDefaultParams.ts#L89
 */
const extractUoUBIParams = ({
  controllerConfig: { appParams, platformAPIs },
}: FlowApi) => {
  const loggerFactory = platformAPIs.biLoggerFactory?.() as any;
  const defaults = loggerFactory?.loggerClientFactory?._defaults;

  return {
    appId: appParams.appDefinitionId,
    bsi: defaults?.bsi?.(),
    instanceId: appParams.instanceId,
    metaSiteId: platformAPIs.bi?.metaSiteId,
    siteOwnerId: platformAPIs.bi?.ownerId,
    visitorId: platformAPIs.bi?.visitorId,
  };
};

export const getControllerProps = (
  flowAPI: FlowApi,
  store: Store,
): ControllerProps => ({
  ...store.getState(),
  ...getActionHandlers(store),
  ...(flowAPI.controllerConfig.platformAPIs.bi?.viewerName ===
    'thunderbolt' && { fitToContentHeight: true }),
  instance: flowAPI.controllerConfig.appParams.instance,
  defaultBIParams: extractUoUBIParams(flowAPI),
  isRendered: true,
});

export const getSEODataWithProfileURL = async (
  wixCodeApi: WixCodeApi,
  seoData: MemberSEOData,
) => {
  const { location } = wixCodeApi;

  return {
    ...seoData,
    member: {
      ...seoData.member,
      profile: {
        ...seoData.member.profile,
        profilePageUrl: location.url,
      },
    },
  };
};

export const getControllerContext = ({
  flowAPI,
  controllerConfig: { wixCodeApi, setProps },
}: ControllerParams): ControllerContext => {
  const settingsListener = createSettingsListener();
  const widgetPluginService = createMembersAreaWidgetPluginService();
  const warmupDataService = initWarmupDataService(wixCodeApi.window.warmupData);
  const membersAboutMiddlewareService = new MembersAboutMiddlewareService({
    httpClient: flowAPI.httpClient,
    errorHandler: flowAPI.errorHandler,
  });

  return {
    flowAPI,
    wixCodeApi,
    setProps,
    widgetPluginService,
    warmupDataService,
    settingsListener,
    membersAboutMiddlewareService,
  };
};

const getMembersAreaPublicAPI = (
  flowAPI: FlowApi,
  wixCodeApi: WixCodeApi,
): Promise<MembersAreaApi> | undefined => {
  return wixCodeApi.site.getPublicAPI(MEMBERS_AREA).catch((error) => {
    flowAPI.panoramaClient?.errorMonitor().reportError(error as Error);
  });
};

const getIsMembersAreaSeoEnabled = async (
  flowAPI: FlowApi,
  wixCodeApi: WixCodeApi,
): Promise<boolean> => {
  const membersAreaPublicAPI = await getMembersAreaPublicAPI(
    flowAPI,
    wixCodeApi,
  );

  return membersAreaPublicAPI?.getIsMembersAreaSeoEnabled() ?? false;
};

const maybeCallRenderSeoTags = async ({
  flowAPI,
  wixCodeApi,
  seoData,
  isViewer,
  isWidgetPlugin,
  site,
}: MaybeCallRenderSeoTagsOptions) => {
  if (!isViewer || !seoData || isWidgetPlugin) {
    return;
  }

  const isRenderSEOTagsEnabled = await getIsMembersAreaSeoEnabled(
    flowAPI,
    wixCodeApi,
  );

  if (!isRenderSEOTagsEnabled) {
    return;
  }

  return renderSEOTags({ wixCodeApi, seoData, site });
};

export const initialiseAboutWidget = async ({
  flowAPI,
  wixCodeApi,
  widgetPluginService,
  warmupDataService,
  settingsListener,
  setProps,
  membersAboutMiddlewareService,
}: ControllerContext) => {
  const { initialState, seoData } = await getInitialData({
    flowAPI,
    widgetPluginService,
    membersAboutMiddlewareService,
    warmupDataService,
  });

  const store = createStore(
    {
      flowAPI,
      wixCodeApi,
      widgetPluginService,
      warmupDataService,
      membersAboutMiddlewareService,
    },
    initialState,
  );
  const isWidgetPlugin = widgetPluginService.getIsWidgetPlugin();
  const { isViewer } = flowAPI.environment;

  store.subscribe(() => setProps(store.getState()));
  setProps(getControllerProps(flowAPI, store));
  registerUserListeners(flowAPI, store, isWidgetPlugin);
  registerControllerListeners(flowAPI, settingsListener, store);
  registerWidgetPluginHostListeners({
    wixCodeApi,
    widgetPluginService,
    isViewer,
    store,
    seoData,
  });

  await maybeCallRenderSeoTags({
    flowAPI,
    wixCodeApi,
    seoData,
    isViewer,
    isWidgetPlugin,
    site: initialState.site,
  });
};

export const renderSEOTags = async ({
  site,
  seoData,
  wixCodeApi,
}: RenderSEOTagsOptions) => {
  const isBlogContributor = getIsBlogContributor(
    site.isBlogInstalled,
    seoData.member.roles,
  );

  const itemType = isBlogContributor
    ? ITEM_TYPES.MEMBERS_AREA_AUTHOR_PROFILE
    : ITEM_TYPES.MEMBERS_AREA_PROFILE;

  const itemData = await getSEODataWithProfileURL(wixCodeApi, seoData);

  wixCodeApi.seo.renderSEOTags({
    itemType,
    itemData,
  });
};
